import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Share2, Copy, Check, Twitter, Facebook, MessageCircle, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { buildShareUrl, buildSocialShareUrl, pinterestDescription } from "@/lib/share-url";
import { openExternalUrl, copyToClipboard } from "@/lib/native-share";
import { shareWithImages } from "@/lib/share-image";
import { isNativePlatform } from "@/lib/platform";

interface ShareDesignPopoverProps {
  designId: string;
  designStyle: string;
  roomType: string;
  imageUrl: string;
  variations?: string[];
}

const PinterestIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.43.22-.93 1.4-5.94 1.4-5.94s-.36-.72-.36-1.78c0-1.67.97-2.92 2.17-2.92 1.03 0 1.52.77 1.52 1.7 0 1.03-.66 2.58-1 4.02-.28 1.2.6 2.18 1.79 2.18 2.15 0 3.8-2.27 3.8-5.54 0-2.9-2.08-4.92-5.05-4.92-3.44 0-5.46 2.58-5.46 5.25 0 1.04.4 2.16.9 2.76.1.12.11.23.08.35-.09.36-.28 1.2-.32 1.36-.05.22-.17.27-.4.16-1.5-.7-2.43-2.88-2.43-4.64 0-3.78 2.75-7.25 7.92-7.25 4.16 0 7.39 2.96 7.39 6.92 0 4.13-2.6 7.46-6.22 7.46-1.21 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15A12 12 0 1 0 12 0z"/></svg>
);

export const ShareDesignPopover = ({ designId, designStyle, roomType, imageUrl, variations }: ShareDesignPopoverProps) => {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [pinterestSubOpen, setPinterestSubOpen] = useState(false);

  const allImages = variations && variations.length > 0 ? variations : [imageUrl];
  const hasMultipleImages = allImages.length > 1;

  const shareUrl = buildShareUrl(`/explore?design=${designId}`);
  // Crawler-friendly link (per-design Open Graph card) used for social targets.
  const socialUrl = buildSocialShareUrl(designId);
  const shareText = `Check out this ${designStyle} ${roomType} design on Decor Canvas`;

  const handleCopy = async () => {
    const ok = await copyToClipboard(shareUrl);
    if (ok) {
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Failed to copy link");
    }
  };

  const sanitize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const handleNativeShare = async () => {
    setSharing(true);
    try {
      const result = await shareWithImages({
        images: allImages,
        url: socialUrl,
        title: shareText,
        text: shareText,
        filenameBase: `decor-canvas-${sanitize(designStyle)}-${sanitize(roomType)}`,
      });
      if (result === "copied") toast.success("Link copied — paste it into your post");
      else if (result === "url") toast.message("Shared the link (image couldn't be attached)");
      setOpen(false);
    } catch {
      // user cancelled or share failed silently
    } finally {
      setSharing(false);
    }
  };

  const openShare = (url: string) => {
    void openExternalUrl(url);
  };

  const encodedUrl = encodeURIComponent(socialUrl);
  const encodedText = encodeURIComponent(shareText);

  const pinterestUrlFor = (img: string) =>
    `https://www.pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodeURIComponent(img)}&description=${encodeURIComponent(pinterestDescription(shareText))}`;

  const hasNativeShare = isNativePlatform() || (typeof navigator !== "undefined" && !!navigator.share);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPinterestSubOpen(false); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="Share design">
          <Share2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground px-2 py-1">
            Share this design{hasMultipleImages ? ` (${allImages.length} images)` : ""}
          </p>

          {hasNativeShare && (
            <Button variant="ghost" size="sm" className="w-full justify-start" onClick={handleNativeShare} disabled={sharing}>
              {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
              {sharing ? "Preparing..." : hasMultipleImages ? `Share all ${allImages.length}...` : "Share..."}
            </Button>
          )}

          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={handleCopy}>
            {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied!" : "Copy link"}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => openShare(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`)}
          >
            <Twitter className="h-4 w-4" />
            Twitter / X
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)}
          >
            <Facebook className="h-4 w-4" />
            Facebook
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => openShare(`https://wa.me/?text=${encodedText}%20${encodedUrl}`)}
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </Button>

          {hasMultipleImages ? (
            <div>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between"
                onClick={() => setPinterestSubOpen((v) => !v)}
              >
                <span className="flex items-center gap-2">
                  <PinterestIcon />
                  Pinterest
                </span>
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${pinterestSubOpen ? "rotate-90" : ""}`} />
              </Button>
              {pinterestSubOpen && (
                <div className="pl-4 space-y-0.5 mt-0.5">
                  {allImages.map((img, i) => (
                    <Button
                      key={i}
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start text-xs h-7"
                      onClick={() => openShare(pinterestUrlFor(img))}
                    >
                      Pin variation {i + 1}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={() => openShare(pinterestUrlFor(allImages[0]))}
            >
              <PinterestIcon />
              Pinterest
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
